import { useCallback, useContext } from 'react';
import ReportGadgetsContext from '../../../../../../contexts/ReportGadgetsContext';
import isV5ChartDef from '../../../../../../types/visTypeCheckers/isV5ChartDef';
import MetricOptionsContext from '../../../../../../contexts/MetricOptionsContext';
import CompoundMetricsContext from '../../../../../../contexts/CompoundMetricsContext';
import metricTypeCheckers from '../../../../../../types/metricTypeCheckers';
import visTypeCheckers from '../../../../../../types/visTypeCheckers';

const useGetFieldGadgetDimensionUsage = () => {
  const { allGadgets } = useContext(ReportGadgetsContext);
  const definitions = allGadgets.map((g) => g.chartDef);
  const { metricOptionsLookup } = useContext(MetricOptionsContext);
  const { getCompoundMetricWithMetricDefs } = useContext(
    CompoundMetricsContext,
  );

  const isForDatasetByMetricId = useCallback(
    (metricId?: string) => {
      if (!metricId) return false;
      const metric = metricOptionsLookup[metricId];
      if (!metric || metricTypeCheckers.isSpecialMetric(metric)) return false;
      if (metricTypeCheckers.isNormalMetric(metric)) return metric.dataType;
      const compoundMetric = getCompoundMetricWithMetricDefs(metric.id);
      if (!compoundMetric) return false;
      const m = compoundMetric.metrics.find((mm) => !!mm.dataType);
      return m?.dataType || false;
    },
    [getCompoundMetricWithMetricDefs, metricOptionsLookup],
  );

  const isMetricForDataset = useCallback(
    (metricId: string, dataset: string) => {
      const dt = isForDatasetByMetricId(metricId);
      return dt === dataset;
    },
    [isForDatasetByMetricId],
  );

  const getFieldGadgetDimensionUsage = useCallback(
    (field: string, dataset: string) => {
      const v5Matches = definitions.filter((def) => {
        if (!isV5ChartDef(def)) return false;
        const isForDataset = def.series.some((s) => {
          const metric = metricOptionsLookup[s.metricId];

          if (!metric || metricTypeCheckers.isSpecialMetric(metric)) {
            return false;
          }

          if (metricTypeCheckers.isNormalMetric(metric)) {
            return metric.dataType === dataset;
          }

          const compoundMetric = getCompoundMetricWithMetricDefs(metric.id);

          if (!compoundMetric) {
            return false;
          }

          return compoundMetric.metrics.some((m) => m.dataType === dataset);
        });

        if (!isForDataset) return false;

        const a = def.dimensionA?.field;
        const b = def.dimensionB?.field;

        return a === field || b === field;
      });

      // Extended visualisations (non-V5 chart defs)
      const extendedMatches = definitions.filter((def) => {
        if (isV5ChartDef(def)) return false;
        if (visTypeCheckers.isPaceMatrix(def)) {
          return (
            def.groupByField === field &&
            isMetricForDataset(def.metricId, dataset)
          );
        } else if (visTypeCheckers.isSingleMetricDateMatrix(def)) {
          return (
            def.groupByField === field &&
            isMetricForDataset(def.metricId, dataset)
          );
        } else if (visTypeCheckers.isGauge(def)) {
          return (
            def.peerGroup === field && isMetricForDataset(def.metricId, dataset)
          );
        } else if (visTypeCheckers.isRankingMatrix(def)) {
          const hasDataset = def.metrics?.some((m) =>
            isMetricForDataset(m.metricId, dataset),
          );
          return def.groupByField === field && !!hasDataset;
        }
        return false;
      });

      // Deduplicate by id across both sets
      const combined = [...v5Matches, ...extendedMatches];
      return combined.filter(
        (d, idx, arr) => arr.findIndex((x) => x.id === d.id) === idx,
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      allGadgets,
      getCompoundMetricWithMetricDefs,
      isMetricForDataset,
      metricOptionsLookup,
    ],
  );

  return getFieldGadgetDimensionUsage;
};

export default useGetFieldGadgetDimensionUsage;
